import Phaser from 'phaser';
import { InteractionContext, Interactable } from '../interfaces/Interactable';
import { Player } from '../objects/Player';
import { DialogueSystem } from './DialogueSystem';
import { InputSystem } from './InputSystem';
import { QuestSystem } from './QuestSystem';
import { ScoreSystem } from './ScoreSystem';
import { AudioSystem } from './AudioSystem';
import { UIStyle } from '../ui/UIStyle';

export class InteractionSystem {
  private interactables: Interactable[] = [];
  private promptText?: Phaser.GameObjects.Text;
  private nearest: Interactable | null = null;
  private ctx: InteractionContext;

  constructor(
    scene: Phaser.Scene,
    player: Player,
    dialogue: DialogueSystem,
    quest: QuestSystem,
    score: ScoreSystem,
  ) {
    this.ctx = { scene, player, dialogue, quest, score };

    this.promptText = scene.add.text(0, 0, '', {
      ...UIStyle.onBrightBg(UIStyle.px(16), '#ffffff', true),
      backgroundColor: '#0a1218ee',
      padding: { x: 12, y: 7 },
    });
    this.promptText.setOrigin(0.5);
    this.promptText.setDepth(120);
    this.promptText.setVisible(false);
  }

  register(interactable: Interactable): void {
    this.interactables.push(interactable);
  }

  registerAll(items: Interactable[]): void {
    items.forEach((i) => this.register(i));
  }

  update(player: Player, input: InputSystem): void {
    if (this.ctx.dialogue.isActive()) {
      this.promptText?.setVisible(false);
      player.setMovementEnabled(false);
      return;
    }

    player.setMovementEnabled(true);
    this.nearest = this.findNearest(player);

    if (this.nearest?.canInteract()) {
      const pos = this.nearest.getInteractPosition();
      this.promptText?.setText(this.nearest.getPromptText());
      this.promptText?.setPosition(pos.x, pos.y - 60);
      this.promptText?.setVisible(true);

      if (input.isActionJustPressed()) {
        if (this.nearest) {
          AudioSystem.playSfx('INTERACT');
          this.nearest.onInteract(this.ctx);
        }
      }
    } else {
      this.promptText?.setVisible(false);
    }
  }

  private findNearest(player: Player): Interactable | null {
    let best: Interactable | null = null;
    let bestDist = Infinity;

    for (const item of this.interactables) {
      if (!item.canInteract()) continue;
      const pos = item.getInteractPosition();
      const dist = Phaser.Math.Distance.Between(player.x, player.y, pos.x, pos.y);
      if (dist <= item.getInteractionRadius() && dist < bestDist) {
        bestDist = dist;
        best = item;
      }
    }
    return best;
  }

  destroy(): void {
    this.promptText?.destroy();
    this.interactables = [];
  }
}
